import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import MenteeResponseCard from './MenteeResponseCard.jsx';
import { storage, addAuditEntry, calculateWorkingHours, OWL_LOGO, EMAIL_CONFIG, FIREBASE_CONFIG, detectHarmContent, detectUnconstructiveContent, detectConnotationIssues, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase } from '../shared.jsx';
import AiContentReportButton from './AiContentReportButton.jsx';


function IdentifyDefineStep({ conflict, user, canEdit, onUpdate, onReturnToOverview }) {
  const toast = useToast();
  const showCrisisModal = useCrisisAlert();
  const [paraphrase, setParaphrase] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  // Skip communication voting is handled by handleSkipCommunicationVote
  const [attemptedSubmit, setAttemptedSubmit] = useState(false);
  const [idParaphraseWarning, setIdParaphraseWarning] = useState(null);
  const [connotationWarning, setConnotationWarning] = useState(null);
  const [upsetRating, setUpsetRating] = useState(null);
  // Challenges — practical constraints that may affect resolution
  const [challengeText, setChallengeText] = useState('');
  const [submittingChallenge, setSubmittingChallenge] = useState(false);

  // Paraphrase quality detection — warns if user is stating their own case instead of paraphrasing
  const detectNonParaphrase = (text, originalStatement) => {
    if (!text || text.length < 25 || !originalStatement) return null;
    const origStr = typeof originalStatement === 'string' ? originalStatement : (originalStatement?.text || originalStatement?.description || JSON.stringify(originalStatement) || '');
    if (!origStr) return null;
    const lower = text.toLowerCase().trim();
    const originalLower = origStr.toLowerCase().trim();
    // Positive indicators that it IS a paraphrase — check first
    // Intentionally broad to avoid false positives on genuine paraphrasing
    const paraphraseIndicators = [
      // Second-person reflection ("you feel...", "you're saying...")
      /\b(you (feel|felt|are feeling|said|mentioned|expressed|shared|believe|think|want|wanted|need|needed|prefer|described|explained|stated|indicated|brought up|pointed out|raised|highlighted|noted|conveyed|communicated|meant|were saying|are saying))\b/i,
      /\b(you'?re (saying|feeling|expressing|concerned|frustrated|worried|upset|afraid|hoping|trying to|describing|explaining|pointing out|asking for|looking for|wanting|needing))\b/i,
      // Active listening / reflection frames
      /\b(what (i|you)'?re? (hearing|saying|understanding|getting|gathering|picking up))\b/i,
      /\b(it sounds like|sounds like you|what i hear|i hear you saying|i hear that|what i'?m hearing)\b/i,
      /\b(it seems like|seems like you|it appears that|from what you'?re saying)\b/i,
      /\b(if i understand|if i'?m understanding|let me (make sure|see if) i understand)\b/i,
      /\b(so (what )?you'?re (saying|telling|feeling|expressing)|so you (feel|think|believe|want|need|said))\b/i,
      /\b(so basically|so essentially|so in other words|in other words)\b/i,
      // Possessive reflection ("your concern...", "your perspective...")
      /\b(your (concern|feeling|perspective|point|frustration|worry|position|view|desire|wish|hope|issue|argument|side|experience|take|stance|reasoning|thought|preference|need|request))\b/i,
      // Third-person reflection (for observers)
      /\b(they (feel|felt|said|mentioned|expressed|shared|believe|think|want|need))\b/i,
      /\b(their (concern|feeling|perspective|point|position|view|argument|frustration|side))\b/i,
      // Perspective-taking frames
      /\b(from (your|their) (perspective|point of view|standpoint|end|side|angle))\b/i,
      /\b(you (are|were) (concerned|worried|frustrated|upset|hoping|saying|feeling|experiencing|dealing with|going through))\b/i,
      /\b(you'?d like|you would like|you prefer|you value|you wish|you hoped|you expected)\b/i,
      /\b(what matters to you|important to you|you care about|matters most to you)\b/i,
      /\b(the other (person|party|participant|side) (feels?|wants?|said|believes?|thinks?|needs?|expressed|is))\b/i,
      /\b(this person (feels?|wants?|said|believes?|thinks?|needs?|is))\b/i,
      /\b(understands? that|understanding is|in (their|your) (words|view|eyes|opinion))\b/i,
      // "I" statements that are actually paraphrase frames (not self-expression)
      /\b(i understand (that|you|your|why|how)|i'?m understanding)\b/i,
      /\b(i hear (you|that|what)|i can (see|hear|understand|tell) (that|why|how|you))\b/i,
      /\b(i (get|see) (that|what|where|why|how) you)\b/i,
      /\b(i think (you'?re|what you'?re) (saying|feeling|expressing|getting at|trying to say))\b/i,
      /\b(i (gather|sense|notice|recognize) (that|you))\b/i,
      /\b(what i(('?m)| am) (hearing|understanding|getting|gathering|taking away) (is|from))\b/i,
      /\b(the way i understand (it|this|your))\b/i,
      /\b(correct me if i'?m wrong|if i got (this|that|it) right)\b/i,
      // Summarizing and restating
      /\b(to (summarize|sum up|restate|put it another way|recap))\b/i,
      /\b(the (main|key|core|central|big|bottom) (point|issue|concern|thing|takeaway) (is|seems|here|you))\b/i,
      /\b(what you'?re (really )?saying|what this (comes|boils) down to)\b/i,
      // Problem-statement-specific: restating the conflict itself
      /\b(the (conflict|disagreement|dispute|issue|situation) (is|involves|concerns|centers|revolves))\b/i,
      /\b(both (parties|sides|people|participants) (are|want|feel|need|disagree))\b/i,
      /\b(one (person|party|side) (wants?|feels?|believes?|thinks?|needs?) .+ (while|whereas|but|and) the other)\b/i,
    ];
    const hasParaphraseLanguage = paraphraseIndicators.some(r => r.test(lower));

    // Indicators that the user is stating their OWN case instead of paraphrasing
    const selfStatementIndicators = [
      /^(i feel|i think|i believe|i need|i want|i don'?t|i am |i'm |i have |i had |i was |i've )/i,
      /\bi (don'?t|do not) (care|want|need|like|agree|think)\b/i,
      /\bi (just|really|honestly) (don'?t|want|need|think|feel)\b/i,
      /^(my (feeling|opinion|perspective|view|point|problem|issue|concern) is)/i,
      /^(the (problem|issue|thing) is that i)\b/i,
      /^because i feel/i,
      /\b(i couldn'?t care less|i could care less|whatever|who cares)\b/i,
    ];
    const argumentIndicators = [
      /\b(you (always|never|should|shouldn'?t|need to|have to|must|just want|only want|only care))\b/i,
      /\b(you'?re (wrong|being (difficult|selfish|unreasonable)|not listening|just|only|so |such ))\b/i,
      /\b(that'?s not (true|fair|right|correct))\b/i,
      /\b(you don'?t (understand|listen|care|get it|know|realize|see))\b/i,
      /\b(the real (problem|issue) is)\b/i,
      /\b(actually|obviously|clearly|unfair),?\s/i,
      /\b(you just want|you only want|all you (want|care|do|think))\b/i,
      /\b(you'?re trying to|you want to (cause|make|start|create))\b/i,
      /\b(give me a break|get over it|grow up|deal with it|stop being)\b/i,
      /\b(this is (stupid|dumb|pointless|ridiculous|waste))\b/i,
      /\b(doesn'?t matter|don'?t matter|no one cares|nobody cares)\b/i,
      /\b(shut up|leave me alone|back off|mind your)\b/i,
      /\b(blame|fault|wrong with you|problem with you)\b/i,
    ];
    const hasSelfStatement = selfStatementIndicators.some(r => r.test(lower));
    const hasArgument = argumentIndicators.some(r => r.test(lower));

    // Mixed content: user wrote a valid paraphrase BUT also included argument/self-advocacy
    // Check this BEFORE the pure-paraphrase short-circuit
    if (hasParaphraseLanguage && hasArgument) {
      return { type: 'argument', message: 'Your response includes an argument mixed in with the paraphrase. This section is only for restating the problem. Remove the parts where you argue your position.', tip: 'Keep only the paraphrase portion. You\'ll get to share your perspective in the Communication phase.' };
    }
    if (hasParaphraseLanguage && hasSelfStatement) {
      return { type: 'mixed', message: 'Good start on the paraphrase! But it looks like you also included your own perspective or preferences. This section is just for restating the problem. You\'ll share your side in the Communication phase.', tip: 'Remove the parts about your own feelings or preferences and keep just the paraphrase of the conflict.' };
    }

    // Pure paraphrase with no argument/self-statement indicators — looks good
    if (hasParaphraseLanguage) return null;

    // Semantic similarity — be generous since good paraphrases USE different words
    const cleanOriginal = originalLower.replace(/[.,!?;:'"()-]/g, '');
    const cleanText = lower.replace(/[.,!?;:'"()-]/g, '');
    const stopWords = new Set(['that', 'this', 'with', 'from', 'they', 'them', 'their', 'have', 'been', 'were', 'would', 'could', 'should', 'about', 'which', 'there', 'these', 'those', 'being', 'when', 'what', 'your', 'will', 'also', 'just', 'more', 'some', 'very', 'than', 'into', 'only', 'each', 'much', 'does', 'like', 'make', 'over', 'such', 'take', 'because', 'feel']);
    const originalWords = new Set(cleanOriginal.split(/\s+/).filter(w => w.length > 3 && !stopWords.has(w)));
    const textWords = cleanText.split(/\s+/).filter(w => w.length > 3 && !stopWords.has(w));
    const sharedWords = textWords.filter(w => originalWords.has(w));
    // Stem-level matching (first 5 chars) catches frustrated/frustration, comfort/comfortable etc.
    const originalStems = new Set([...originalWords].map(w => w.substring(0, 5)));
    const stemMatches = textWords.filter(w => originalStems.has(w.substring(0, 5)));
    const combinedMatches = new Set([...sharedWords, ...stemMatches]);
    const similarity = originalWords.size > 0 ? combinedMatches.size / originalWords.size : 0;
    const lowSimilarity = similarity < 0.08 && text.length > 60;

    // Arguments flagged first — clearly not paraphrasing
    if (hasArgument) return { type: 'argument', message: 'This sounds like you\'re responding to or arguing against the other person\'s statement. Paraphrasing means reflecting back what they expressed, not reacting to it.', tip: 'Focus on their words: "You expressed that..." or "You\'re saying that...". You\'ll get to share your perspective later.' };
    // Self-statements only flagged if ALSO low similarity (avoids false positives on "I understand that you...")
    if (hasSelfStatement && (lowSimilarity || similarity < 0.15)) return { type: 'self_statement', message: 'It looks like you\'re sharing your own feelings or perspective. This section is for paraphrasing: restating what the other person said in your own words to show you understood them.', tip: 'Try starting with: "You feel..." or "What I hear you saying is..." or "Your concern is..."' };
    // Off-topic only with very low similarity and no self-statement (avoid double-flagging)
    if (lowSimilarity && !hasSelfStatement) return { type: 'off_topic', message: 'Your response doesn\'t seem to relate to the problem statement. A paraphrase should restate the key points using your own words.', tip: 'Re-read the problem statement above and try to capture the main issue in your own words.' };
    return null;
  };

  const [harmWarning, setHarmWarning] = useState(null);

  const handleIdParaphraseChange = (newText) => {
    setParaphrase(newText);
    const warning = detectNonParaphrase(newText, conflict.problemStatement || conflict.description || '');
    setIdParaphraseWarning(warning);
    // Reset connotation warning when text changes (user may be revising)
    if (connotationWarning && !connotationWarning._dismissed) {
      setConnotationWarning(null);
    }
    // Check for unconstructive/nasty content
    if (newText.length > 5) {
      const toneCheck = detectUnconstructiveContent(newText);
      if (toneCheck.isUnconstructive) {
        setHarmWarning(toneCheck.reason);
      } else {
        const crisisCheck = detectHarmContent(newText);
        setHarmWarning(crisisCheck.detected
          ? 'This message may indicate someone needs support.'
          : null);
      }
    } else {
      setHarmWarning(null);
    }
  };

  // AI Paraphrase Assistance State for problem statement
  const [idAiSuggestions, setIdAiSuggestions] = useState([]);
  const [idAiLoading, setIdAiLoading] = useState(false);
  const [idAiError, setIdAiError] = useState(null);
  const [idAiPanelOpen, setIdAiPanelOpen] = useState(false);
  const [idSelectedSuggestion, setIdSelectedSuggestion] = useState(null);
  const [idAiUsed, setIdAiUsed] = useState(false);

  const aiAssistEnabled = conflict.aiParaphraseAssist !== false;

  const isMentee = conflict.mentees?.some(m => m.id === user.id || m.email.toLowerCase() === user.email.toLowerCase()) ||
                   conflict.createdBy === user.id;
  const userResponse = conflict.steps?.identifyDefine?.data?.[user.id];

  // Profanity check function
  const profanityList = [
    'fuck', 'fucking', 'fucked', 'fucker', 'shit', 'shitty', 'damn', 'bitch', 'bitchy', 'asshole', 'bastard', 'crap', 
    'piss', 'pissed', 'dick', 'cock', 'pussy', 'slut', 'whore', 'fag', 'nigger',
    'cunt', 'twat', 'wank', 'wanker', 'bloody', 'bugger', 'arse', 'bollocks', 'prick',
    'motherfucker', 'motherfucking', 'bullshit', 'horseshit', 'dumbass', 'jackass', 'dipshit'
  ];

  const containsProfanity = (text) => {
    const lowerText = text.toLowerCase();
    return profanityList.some(word => {
      const regex = new RegExp(`\\b${word}\\b`, 'i');
      return regex.test(lowerText);
    });
  };

  const handleSubmitParaphrase = async () => {
    setAttemptedSubmit(true);
    
    if (paraphrase.length < 50) {
      toast('Paraphrase must be at least 50 characters');
      return;
    }

    if (paraphrase.length > 250) {
      toast('Paraphrase must be 250 characters or less');
      return;
    }

    if (containsProfanity(paraphrase)) {
      toast('Please remove profanity from your paraphrase');
      return;
    }

    // Block harmful/nasty content
    const toneCheck = detectUnconstructiveContent(paraphrase);
    if (toneCheck.isUnconstructive) {
      toast(toneCheck.reason || 'Please keep your paraphrase respectful and constructive.');
      return;
    }

    // Check for inflammatory connotations — warn but don't block
    const connotation = detectConnotationIssues(paraphrase);
    if (connotation.hasIssues && !connotationWarning?._dismissed) {
      setConnotationWarning(connotation);
      return;
    }

    if (upsetRating === null) {
      toast('Please rate how upsetting this issue is for you before submitting.');
      return;
    }

    if (idAiUsed && !meetsIdEditRequirement(paraphrase)) {
      toast('You must edit the AI suggestion more before submitting. Change at least 10 characters and make it your own.');
      return;
    }

    if (!acknowledged) {
      toast('Please acknowledge that you have read the problem statement');
      return;
    }

    // Warn if paraphrase quality detection flagged an issue
    if (idParaphraseWarning) {
      // Arguments are BLOCKED — user must revise before submitting
      if (idParaphraseWarning.type === 'argument') {
        toast('This looks like an argument, not a paraphrase. Please rewrite it to reflect the other person\'s perspective before submitting. Try starting with "You expressed that..." or "You\'re saying that..."');
        return;
      }
      // Self-statements, mixed, and off-topic get a confirm warning (can still submit)
      const warningMessages = {
        self_statement: 'It looks like you\'re sharing your own perspective instead of paraphrasing. A paraphrase should restate what the problem statement says.\n\nAre you sure you want to submit this?',
        mixed: 'It looks like you included your own perspective along with the paraphrase. This section is just for restating the problem. You\'ll share your side in the Communication phase.\n\nAre you sure you want to submit this as-is?',
        off_topic: 'Your response doesn\'t seem to closely relate to the problem statement.\n\nAre you sure you want to submit this?'
      };
      if (!window.confirm(warningMessages[idParaphraseWarning.type] || 'Are you sure you want to submit this?')) {
        return;
      }
    }

    const updated = {
      ...conflict,
      steps: {
        ...conflict.steps,
        identifyDefine: {
          ...conflict.steps.identifyDefine,
          data: {
            ...conflict.steps.identifyDefine.data,
            [user.id]: {
              paraphrase,
              acknowledged,
              upsetRating,
              timestamp: new Date().toISOString(),
              userName: user.name,
              userEmail: user.email,
              ...(idAiUsed && { aiAssisted: true })
            }
          }
        }
      },
      updatedAt: new Date().toISOString(),
      updatedBy: user.id
    };

    addAuditEntry(updated, user.id, user.name, 'Submitted paraphrase', 'Identify & Define phase');
    await storage.set(`conflict:${conflict.id}`, updated, true);
    // Harm detection — check after saving (never block the user)
    const harmResult = detectHarmContent(paraphrase);
    if (harmResult.detected) {
      showCrisisModal(harmResult.type);
      notifyMentorOfHarmDetection(conflict, user, harmResult.type, 'Identify & Define paraphrase');
    }
    await onUpdate();
    // User has submitted their acknowledgment — return to overview to see next action
    if (onReturnToOverview) onReturnToOverview();
  };

  const handleSkipCommunicationVote = async (skipCommunication) => {
    
    try {
      const currentVotes = conflict.steps?.identifyDefine?.skipCommunicationVotes || {};
      const updatedVotes = {
        ...currentVotes,
        [user.id]: {
          skip: skipCommunication,
          userId: user.id,
          userName: user.name,
          userEmail: user.email,
          timestamp: new Date().toISOString()
        }
      };

      const menteeIds = conflict.mentees?.map(m => m.id) || [];
      const allVoted = menteeIds.every(id => updatedVotes[id] !== undefined) || 
                       Object.keys(updatedVotes).length >= menteeIds.length;
      const anyVotedNo = Object.values(updatedVotes).some(v => !v.skip);
      const allVotedYes = allVoted && !anyVotedNo;

      // Determine new status only when all have voted
      let newStatus = conflict.status;
      let statusEntry = null;
      
      if (anyVotedNo) {
        // Any "no" immediately moves to communicate
        newStatus = 'communicate';
        statusEntry = { status: 'communicate', timestamp: new Date().toISOString() };
      } else if (allVotedYes) {
        // All agreed to skip — go to explore-alternatives
        newStatus = 'explore-alternatives';
        statusEntry = { status: 'explore-alternatives', timestamp: new Date().toISOString() };
      }

      const updated = {
        ...conflict,
        steps: {
          ...conflict.steps,
          identifyDefine: {
            ...conflict.steps.identifyDefine,
            skipCommunicationVotes: updatedVotes,
            // Clear old resolutionCheck if any
            resolutionCheck: conflict.steps?.identifyDefine?.resolutionCheck || null
          }
        },
        ...(statusEntry && {
          status: newStatus,
          statusHistory: [
            ...conflict.statusHistory,
            statusEntry
          ]
        }),
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };

      addAuditEntry(updated, user.id, user.name, 'Voted on skip communication', skipCommunication ? 'Voted to skip to Explore Alternatives' : 'Voted to continue to Communication');
      const saved = await storage.set(`conflict:${conflict.id}`, updated, true);
      
      if (saved) {
        await onUpdate();
        
        if (anyVotedNo) {
          toast('A participant chose to continue with communication. Moving to the Communication step.');
          if (onReturnToOverview) onReturnToOverview();
        } else if (allVotedYes) {
          toast('All participants agreed to skip communication! Moving to Explore Alternatives.');
          if (onReturnToOverview) onReturnToOverview();
        } else {
          toast('Your vote has been recorded. Waiting for other participants to vote.');
          if (onReturnToOverview) onReturnToOverview();
        }
      } else {
        toast('Failed to save vote. Please try again.');
      }
    } catch (error) {
      console.error('Error in handleSkipCommunicationVote:', error);
      toast('An error occurred. Please try again.');
    }
  };

  // === CHALLENGES — practical constraints that may affect resolution ===
  const challenges = conflict.steps?.identifyDefine?.challenges || [];
  const myChallenges = challenges.filter(c => c.userId === user.id);
  const maxChallenges = 3;

  const handleAddChallenge = async () => {
    if (!challengeText.trim() || challengeText.trim().length < 10) {
      toast('Please describe the challenge in at least 10 characters');
      return;
    }
    if (challengeText.trim().length > 200) {
      toast('Challenge must be 200 characters or less');
      return;
    }
    if (myChallenges.length >= maxChallenges) {
      toast(`You can add up to ${maxChallenges} challenges`);
      return;
    }
    if (containsProfanity(challengeText)) {
      toast('Please remove profanity');
      return;
    }
    const toneCheck = detectUnconstructiveContent(challengeText);
    if (toneCheck.isUnconstructive) {
      toast(toneCheck.reason || 'Please keep it respectful and constructive.');
      return;
    }
    setSubmittingChallenge(true);
    try {
      const newChallenge = {
        id: `ch-${Date.now()}`,
        text: challengeText.trim(),
        userId: user.id,
        userName: user.name,
        userEmail: user.email,
        timestamp: new Date().toISOString()
      };
      const updated = {
        ...conflict,
        steps: {
          ...conflict.steps,
          identifyDefine: {
            ...conflict.steps.identifyDefine,
            challenges: [...challenges, newChallenge]
          }
        },
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };
      addAuditEntry(updated, user.id, user.name, 'Added challenge', challengeText.trim().substring(0, 60));
      await storage.set(`conflict:${conflict.id}`, updated, true);
      setChallengeText('');
      await onUpdate();
      toast('Challenge added!');
    } catch (e) {
      console.error('Error adding challenge:', e);
      toast('Failed to save. Please try again.');
    }
    setSubmittingChallenge(false);
  };

  const handleRemoveChallenge = async (challengeId) => {
    const ch = challenges.find(c => c.id === challengeId);
    if (!ch || ch.userId !== user.id) return;
    if (!window.confirm('Are you sure you want to remove this challenge?')) return;
    try {
      const updated = {
        ...conflict,
        steps: {
          ...conflict.steps,
          identifyDefine: {
            ...conflict.steps.identifyDefine,
            challenges: challenges.filter(c => c.id !== challengeId)
          }
        },
        updatedAt: new Date().toISOString(),
        updatedBy: user.id
      };
      addAuditEntry(updated, user.id, user.name, 'Removed challenge', ch.text.substring(0, 60));
      await storage.set(`conflict:${conflict.id}`, updated, true);
      await onUpdate();
      toast('Challenge removed');
    } catch (e) {
      toast('Failed to remove. Please try again.');
    }
  };

  // === AI PROBLEM STATEMENT PARAPHRASE ASSISTANCE ===
  
  const callIdAiFunction = (payload) => {
    const projectNumber = FIREBASE_CONFIG.messagingSenderId;
    const functionUrl = `https://generateparaphrasesuggestions-${projectNumber}.us-central1.run.app`;
    const bodyStr = JSON.stringify({ ...payload, userId: user.id });

    return new Promise(async (resolve, reject) => {
      const token = await window.firebaseAuth?.currentUser?.getIdToken(false).catch(() => null);
      try {
        const response = await fetch(functionUrl, {
          method: "POST",
          mode: "cors",
          credentials: "omit",
          headers: { "Content-Type": "application/json", "Accept": "application/json", "Authorization": `Bearer ${token}` },
          body: bodyStr
        });
        if (!response.ok) {
          let errorMsg = `Server error: ${response.status}`;
          try { const ed = await response.json(); if (ed.error) errorMsg = ed.error; } catch(e) {}
          throw new Error(errorMsg);
        }
        return resolve(await response.json());
      } catch (fetchError) {
      }
      try {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", functionUrl, true);
        xhr.setRequestHeader("Content-Type", "application/json");
        xhr.setRequestHeader("Accept", "application/json");
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.timeout = 30000;
        xhr.onload = function() {
          if (xhr.status >= 200 && xhr.status < 300) {
            try { resolve(JSON.parse(xhr.responseText)); } catch(e) { reject(new Error('Invalid JSON')); }
          } else {
            let errorMsg = `Server error: ${xhr.status}`;
            try { const ed = JSON.parse(xhr.responseText); if (ed.error) errorMsg = ed.error; } catch(e) {}
            reject(new Error(errorMsg));
          }
        };
        xhr.onerror = () => reject(new Error('Network request failed'));
        xhr.ontimeout = () => reject(new Error('Request timed out'));
        xhr.send(bodyStr);
      } catch (xhrError) {
        reject(new Error(`Both fetch and XHR failed: ${xhrError.message}`));
      }
    });
  };

  const generateIdAiSuggestions = async () => {
    setIdAiLoading(true);
    setIdAiError(null);
    setIdAiPanelOpen(true);
    
    try {
      const ps = conflict.problemStatement || {};
      const problemContext = [
        ps.who ? `Who: ${ps.who}` : '',
        ps.what ? `What: ${ps.what}` : '',
        ps.where ? `Where: ${ps.where}` : '',
        ps.when ? `When: ${ps.when}` : '',
        ps.how ? `How: ${ps.how}` : ''
      ].filter(Boolean).join('\n');
      
      const data = await callIdAiFunction({
        statement: problemContext,
        conflictId: conflict.id,
        type: 'identify-paraphrase'
      });
      
      if (data.suggestions && Array.isArray(data.suggestions) && data.suggestions.length === 3) {
        setIdAiSuggestions(data.suggestions);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error) {
      console.error('AI ID suggestion failed:', error);
      const msg = error.message?.includes('429') || error.message?.includes('Rate limit')
        ? 'Rate limit reached. Please wait a bit.'
        : 'Failed to generate suggestions. Please try again or write your own.';
      setIdAiError(msg);
    } finally {
      setIdAiLoading(false);
    }
  };

  const meetsIdEditRequirement = (text) => {
    if (!idAiUsed || idAiSuggestions.length === 0) return true;
    for (const suggestion of idAiSuggestions) {
      if (suggestion.includes(text) || text === suggestion) return false;
    }
    if (idSelectedSuggestion !== null) {
      const original = idAiSuggestions[idSelectedSuggestion];
      let diffCount = 0;
      const maxLen = Math.max(text.length, original.length);
      for (let i = 0; i < maxLen; i++) {
        if (text[i] !== original[i]) diffCount++;
      }
      if (diffCount < 10) return false;
    }
    return true;
  };


  // === SECTION RENDERERS ===

  const sectionProblemStatement = (
    <div className="bg-white rounded-lg p-6 border-2 border-amber-200">
      <h4 className="font-medium text-amber-800 mb-4 flex items-center gap-2">
        <FileText className="w-5 h-5" />
        Problem Statement {userResponse ? '(Reference)' : 'to Paraphrase'}
      </h4>
      <div className="space-y-3 text-stone-700">
        <div><span className="font-medium text-stone-800">Who:</span> {conflict.problemStatement.who}</div>
        <div><span className="font-medium text-stone-800">What:</span> {conflict.problemStatement?.what || 'Not specified'}</div>
        <div><span className="font-medium text-stone-800">Where:</span> {conflict.problemStatement.where}</div>
        <div><span className="font-medium text-stone-800">When:</span> {conflict.problemStatement.when}</div>
        <div><span className="font-medium text-stone-800">How:</span> {conflict.problemStatement.how}</div>
      </div>
    </div>
  );

  const hasAnyParaphrases = Object.keys(conflict.steps?.identifyDefine?.data || {}).length > 0;

  const sectionStep1 = (
    <div>
      <h4 className="font-medium text-stone-800 mb-3">Step 1: Restate the Problem</h4>
      <p className="text-sm text-stone-600 mb-4">In 250 characters or less, paraphrase your understanding of the conflict.</p>

      {/* Submission form — shown only if user hasn't submitted yet */}
      {!userResponse && isMentee && canEdit && (
        <div className="mb-6">
          <div className="relative">
            <textarea value={paraphrase} onChange={(e) => handleIdParaphraseChange(e.target.value)}
              className="w-full px-4 py-3 pr-12 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 mb-2"
              placeholder="Paraphrase the problem statement..." rows={4} maxLength={250} />
            <VoiceInputButton onResult={(text) => handleIdParaphraseChange(paraphrase + (paraphrase ? ' ' : '') + text)} className="absolute right-2 top-2" />
          </div>
          {aiAssistEnabled && (
            <div className="mb-3">
              {!idAiPanelOpen && idAiSuggestions.length === 0 && (
                <button onClick={generateIdAiSuggestions} disabled={idAiLoading}
                  className="w-full p-3 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg text-sm text-blue-700 font-medium transition-colors flex items-center justify-center gap-2">
                  {idAiLoading ? (<><Loader2 className="w-4 h-4 animate-spin" />Generating suggestions...</>) : (<><MessageSquare className="w-4 h-4" />Need help? Get AI paraphrase suggestions</>)}
                </button>
              )}
              {idAiPanelOpen && (
                <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-blue-800"><MessageSquare className="w-4 h-4" /><span className="text-sm font-medium">AI Suggestions</span></div>
                    <button onClick={() => setIdAiPanelOpen(false)} className="text-blue-500 hover:text-blue-700" aria-label="Close AI panel"><X className="w-4 h-4" /></button>
                  </div>
                  <p className="text-xs text-blue-600 mb-3">These summarize the Who, What, Where, When, and How. Select one as a starting point, then <strong>edit it to make it your own</strong> (change at least 10 characters).</p>
                  {idAiLoading && (<div className="flex items-center justify-center py-4 text-blue-600"><Loader2 className="w-5 h-5 animate-spin mr-2" />Generating suggestions...</div>)}
                  {idAiError && (<div className="p-2 bg-red-50 rounded border border-red-200 text-xs text-red-700 mb-2">{idAiError}<button onClick={generateIdAiSuggestions} className="ml-2 underline">Try again</button></div>)}
                  {idAiSuggestions.length > 0 && (
                    <div className="space-y-2">
                      {idAiSuggestions.map((suggestion, sIdx) => (
                        <div key={sIdx} className="flex items-stretch gap-1">
                          <button onClick={() => { setParaphrase(suggestion); setIdSelectedSuggestion(sIdx); setIdAiUsed(true); }}
                            className={`flex-1 text-left p-3 rounded-lg border text-sm transition-colors ${idSelectedSuggestion === sIdx ? 'bg-blue-100 border-blue-400 text-blue-900' : 'bg-white border-stone-200 text-stone-700 hover:bg-blue-50 hover:border-blue-300'}`}>
                            <div className="flex items-start gap-2"><span className="text-xs font-medium text-blue-600 mt-0.5 flex-shrink-0">{sIdx + 1}.</span><span className="break-words">{suggestion}</span></div>
                          </button>
                          <div className="flex items-start pt-1">
                            <AiContentReportButton
                              user={user}
                              content={suggestion}
                              context={[{ role: 'user', content: conflict?.problem || conflict?.title || '' }]}
                              source="paraphrase-suggestion"
                            />
                          </div>
                        </div>
                      ))}
                      <button onClick={generateIdAiSuggestions} disabled={idAiLoading} className="w-full text-center text-xs text-blue-600 hover:text-blue-800 py-1">{idAiLoading ? 'Generating...' : 'Regenerate suggestions'}</button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          {/* Paraphrase Quality Warning */}
          {idParaphraseWarning && (
            <div className={`mb-3 p-3 rounded-lg border ${
              idParaphraseWarning.type === 'argument' ? 'bg-red-50 border-red-300' : idParaphraseWarning.type === 'self_statement' || idParaphraseWarning.type === 'mixed' ? 'bg-orange-50 border-orange-200' : 'bg-amber-50 border-amber-200'
            }`}>
              <div className="flex items-start gap-2">
                <AlertCircle className={`w-4 h-4 mt-0.5 flex-shrink-0 ${idParaphraseWarning.type === 'argument' ? 'text-red-600' : 'text-orange-600'}`} />
                <div className="flex-1">
                  <p className={`text-sm font-medium mb-1 ${idParaphraseWarning.type === 'argument' ? 'text-red-800' : 'text-orange-800'}`}>
                    {idParaphraseWarning.type === 'self_statement' && '📝 This looks like your own statement'}
                    {idParaphraseWarning.type === 'argument' && '🚫 This looks like an argument. Please revise'}
                    {idParaphraseWarning.type === 'mixed' && '📝 Good paraphrase, but includes your own perspective'}
                    {idParaphraseWarning.type === 'off_topic' && '🔍 This doesn\'t seem to match the problem statement'}
                  </p>
                  <p className={`text-xs mb-2 ${idParaphraseWarning.type === 'argument' ? 'text-red-700' : 'text-orange-700'}`}>{idParaphraseWarning.message}</p>
                  {idParaphraseWarning.type === 'argument' && (
                    <p className="text-xs text-red-700 font-medium mb-2">You must rewrite this as a paraphrase before you can submit. Reflect back what the other person expressed. You'll get to share your perspective in the next phase.</p>
                  )}
                  <div className={`text-xs p-2 rounded ${idParaphraseWarning.type === 'argument' ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}`}>
                    <strong>💡 Tip:</strong> {idParaphraseWarning.tip}
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Harmful Content Warning */}
          {harmWarning && (
            <div className="mb-3 p-3 rounded-lg border bg-red-50 border-red-200">
              <div className="flex items-start gap-2">
                <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0 text-red-700" />
                <div>
                  <p className="text-sm font-medium mb-1 text-red-800">Harmful language detected</p>
                  <p className="text-xs text-red-700">{harmWarning}</p>
                </div>
              </div>
            </div>
          )}
          {/* Connotation / Word Choice Suggestions */}
          {connotationWarning && connotationWarning.hasIssues && !connotationWarning._dismissed && (
            <div className="mb-3 p-3 rounded-lg border bg-blue-50 border-blue-200">
              <div className="flex items-start gap-2">
                <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0 text-blue-600" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-blue-800 mb-2">Word choice suggestion</p>
                  {connotationWarning.suggestions.map((s, i) => (
                    <div key={i} className="mb-2 last:mb-0">
                      <p className="text-xs text-blue-700 mb-1">{s.context}</p>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded line-through">"{s.found}"</span>
                        <span className="text-blue-600">→</span>
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded">"{s.suggestion}"</span>
                      </div>
                    </div>
                  ))}
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => setConnotationWarning({ ...connotationWarning, _dismissed: true })}
                      className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                    >
                      Keep my wording and submit
                    </button>
                    <span className="text-xs text-blue-600">|</span>
                    <button
                      onClick={() => { setConnotationWarning(null); }}
                      className="text-xs text-green-700 hover:text-green-800 font-medium underline"
                    >
                      I'll revise my text
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {/* Upset Rating */}
          <div className="mb-4 p-4 bg-stone-50 rounded-lg border border-stone-200">
            <p className="text-sm font-medium text-stone-700 mb-2">How upsetting is this issue for you?</p>
            <div className="flex items-center gap-2">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  onClick={() => setUpsetRating(n)}
                  className={`w-10 h-10 rounded-full text-sm font-bold transition-all ${
                    upsetRating === n
                      ? n <= 2 ? 'bg-green-500 text-white ring-2 ring-green-300 shadow-md'
                        : n === 3 ? 'bg-amber-500 text-stone-900 ring-2 ring-amber-300 shadow-md'
                        : 'bg-red-600 text-white ring-2 ring-red-300 shadow-md'
                      : 'bg-white border-2 border-stone-300 text-stone-600 hover:border-amber-400'
                  }`}
                >
                  {n}
                </button>
              ))}
            </div>
            <div className="flex justify-between mt-1.5">
              <span className="text-xs text-stone-500">Not very upsetting</span>
              <span className="text-xs text-stone-500">Extremely upsetting</span>
            </div>
          </div>
          <div className="flex items-center justify-between mb-2">
            <span className={`text-sm ${paraphrase.length > 250 ? 'text-red-700 font-medium' : paraphrase.length >= 50 ? 'text-green-700 font-medium' : 'text-stone-500'}`}>
              {paraphrase.length}/250 characters {paraphrase.length < 50 ? `(need ${50 - paraphrase.length} more)` : ''}
            </span>
          </div>
          {paraphrase.length > 0 && (
            <div className="mb-3 p-3 bg-stone-50 rounded-lg border border-stone-200">
              <div className="text-xs font-medium text-stone-700 mb-2">Requirements:</div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-xs">{paraphrase.length >= 50 && paraphrase.length <= 250 ? <CheckCircle className="w-4 h-4 text-green-700" /> : <AlertCircle className="w-4 h-4 text-stone-500" />}<span className={paraphrase.length >= 50 && paraphrase.length <= 250 ? 'text-green-700' : 'text-stone-600'}>50-250 characters</span></div>
                <div className="flex items-center gap-2 text-xs">{containsProfanity(paraphrase) ? <X className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-green-700" />}<span className={containsProfanity(paraphrase) ? 'text-red-700' : 'text-green-700'}>No profanity</span></div>
                {idAiUsed && (<div className="flex items-center gap-2 text-xs">{meetsIdEditRequirement(paraphrase) ? <CheckCircle className="w-4 h-4 text-green-700" /> : <AlertCircle className="w-4 h-4 text-amber-700" />}<span className={meetsIdEditRequirement(paraphrase) ? 'text-green-700' : 'text-amber-700'}>AI suggestion edited (at least 10 characters changed)</span></div>)}
                <div className="flex items-center gap-2 text-xs">{idParaphraseWarning?.type === 'argument' ? <X className="w-4 h-4 text-red-600" /> : <CheckCircle className="w-4 h-4 text-green-700" />}<span className={idParaphraseWarning?.type === 'argument' ? 'text-red-700' : 'text-green-700'}>{idParaphraseWarning?.type === 'argument' ? 'Rewrite needed: this reads as an argument, not a paraphrase' : 'Paraphrase (not an argument)'}</span></div>
              </div>
            </div>
          )}
          <label className={`flex items-center gap-2 text-sm transition-colors mb-4 ${attemptedSubmit && !acknowledged ? 'text-red-600 font-medium' : acknowledged ? 'text-green-700' : 'text-stone-600'}`}>
            <input type="checkbox" checked={acknowledged} onChange={(e) => { setAcknowledged(e.target.checked); if (e.target.checked) setAttemptedSubmit(false); }} className={`rounded ${attemptedSubmit && !acknowledged ? 'border-2 border-red-500 ring-2 ring-red-200' : ''}`} />
            <span className={attemptedSubmit && !acknowledged ? 'animate-pulse' : ''}>I acknowledge receipt and have read the problem statement{attemptedSubmit && !acknowledged && <span className="text-red-600 ml-1">*</span>}</span>
          </label>
          {attemptedSubmit && !acknowledged && (<div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg"><p className="text-sm text-red-700 flex items-center gap-2"><AlertCircle className="w-4 h-4" />Please check the acknowledgment box to confirm you have read the problem statement</p></div>)}
          <button onClick={handleSubmitParaphrase} disabled={paraphrase.length < 50 || paraphrase.length > 250 || containsProfanity(paraphrase) || (idAiUsed && !meetsIdEditRequirement(paraphrase)) || idParaphraseWarning?.type === 'argument'} className="bg-amber-700 hover:bg-amber-800 disabled:bg-stone-300 disabled:cursor-not-allowed text-white px-6 py-2 rounded-lg font-medium transition-colors">Submit Response</button>
        </div>
      )}

      {/* Waiting message for non-mentee or non-editable */}
      {!userResponse && !(isMentee && canEdit) && (
        <p className="text-stone-500 italic mb-4">Waiting for participants to respond...</p>
      )}

      {/* All participants' paraphrases — review and acknowledge */}
      {hasAnyParaphrases && (
        <>
          {userResponse && (
            <p className="text-sm text-stone-600 mb-4">Review how each participant understands the conflict. Acknowledge and comment if clarification is needed.</p>
          )}
          <div className="space-y-4">
            {Object.entries(conflict.steps?.identifyDefine?.data || {}).map(([usrId, response]) => {
              let mentee = conflict.mentees?.find(m => m.id === usrId);
              if (!mentee && usrId === conflict.createdBy) { const c = conflict.mentees?.find(m => m.id === conflict.createdBy); mentee = c || { id: usrId, email: response.userEmail || (user.id === usrId ? user.email : null), name: response.userName || (user.id === usrId ? user.name : null) }; }
              if (!mentee) { mentee = { id: usrId, email: response.userEmail || 'Unknown', name: response.userName || 'Unknown' }; }
              const acks = response.acknowledgments || [];
              const userAcked = acks.some(a => a.userId === user.id);
              return (<MenteeResponseCard key={usrId} mentee={mentee} response={response} isCurrentUser={usrId === user.id} userHasAcknowledged={userAcked} currentUserId={user.id} currentUserName={user.name} conflict={conflict} onUpdate={onUpdate} isObserver={!isMentee} />);
            })}
          </div>
        </>
      )}
    </div>
  );

  const sectionStatus = (() => {
    const idData = conflict.steps?.identifyDefine?.data || {};
    const mIds = conflict.mentees?.map(m => m.id) || [];
    // Only count responses from current participants (ignore removed participants' data)
    const activeResponses = Object.keys(idData).filter(k => mIds.includes(k));
    const rCount = activeResponses.length;
    if (rCount < mIds.length) return (<div className="p-4 bg-blue-50 border border-blue-200 rounded-lg"><p className="text-sm text-blue-800"><strong>Waiting for paraphrases:</strong> {rCount} of {mIds.length} participants have submitted their understanding.</p></div>);
    let missing = 0, total = 0;
    for (const rid of activeResponses) { const acks = (idData[rid].acknowledgments || []).map(a => a.userId); for (const mid of mIds) { if (mid !== rid) { total++; if (!acks.includes(mid)) missing++; } } }
    if (missing > 0) return (<div className="p-4 bg-amber-50 border border-amber-200 rounded-lg"><p className="text-sm text-amber-800"><strong>Acknowledgments needed:</strong> {total - missing} of {total} complete. Each participant must acknowledge every other participant's paraphrase.</p></div>);
    return null;
  })();

  const sectionSkipVote = (() => {
    const idData = conflict.steps?.identifyDefine?.data || {};
    const mIds = conflict.mentees?.map(m => m.id) || [];
    // Only count responses from current participants (ignore removed participants' data)
    const activeResponses = Object.keys(idData).filter(k => mIds.includes(k));
    const rCount = activeResponses.length;
    if (rCount < mIds.length) return null;
    let allAck = true;
    if (mIds.length > 1) { for (const rid of activeResponses) { const acks = (idData[rid].acknowledgments || []).map(a => a.userId); for (const mid of mIds) { if (mid !== rid && !acks.includes(mid)) { allAck = false; break; } } if (!allAck) break; } }
    if (!allAck) return null;
    const votes = conflict.steps?.identifyDefine?.skipCommunicationVotes || {};
    const allVoted = mIds.every(id => votes[id] !== undefined) || Object.keys(votes).length >= mIds.length;
    const anyNo = Object.values(votes).some(v => !v.skip);
    const vCount = Object.keys(votes).length;
    const myVote = votes[user.id];
    const decided = ['communicate','explore-alternatives','evaluate-select','agree-implement','follow-up'].includes(conflict.status);
    return (
      <div className="border-t border-stone-200 pt-6">
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-green-700 rounded-full flex items-center justify-center flex-shrink-0"><CheckCircle className="w-6 h-6 text-white" /></div>
            <div className="flex-1">
              <h4 className="text-lg font-medium text-green-800 mb-2">Ready to Move Forward</h4>
              <p className="text-green-700 text-sm mb-3">All participants have shared and acknowledged each other's perspectives.</p>
              <p className="text-green-700 text-xs">💡 If <strong>all</strong> agree to skip → Explore Alternatives. If <strong>anyone</strong> wants to communicate → Communication step.</p>
            </div>
          </div>
        </div>
        <h4 className="font-medium text-stone-800 mb-3">Skip Communication?</h4>
        {(decided || anyNo || (allVoted && !anyNo)) ? (
          <div className="p-6 bg-white rounded-lg border-2 border-stone-200">
            <p className="font-medium text-lg text-stone-800 mb-1">{anyNo ? '→ Continuing to Communication Step' : '✓ Skipping to Explore Alternatives'}</p>
            <p className="text-sm text-stone-600 mb-4">{anyNo ? 'At least one participant wants to discuss further.' : 'All participants agreed to skip communication.'}</p>
            {vCount > 0 && (<div className="p-4 bg-stone-50 rounded-lg border border-stone-200"><div className="text-sm font-medium text-stone-700 mb-2">Votes:</div><div className="space-y-2">{Object.values(votes).map((vote, idx) => (<div key={idx} className="flex items-center gap-2 text-sm">{vote.skip ? <ChevronRight className="w-4 h-4 text-green-700" /> : <MessageSquare className="w-4 h-4 text-orange-600" />}<span className={vote.skip ? 'text-green-700' : 'text-orange-700'}>{vote.userEmail || vote.userName}: {vote.skip ? 'Skip' : 'Communicate'}</span><span className="text-stone-500 text-xs">· {new Date(vote.timestamp).toLocaleTimeString()}</span></div>))}</div></div>)}
          </div>
        ) : !myVote && isMentee && canEdit ? (
          <div className="space-y-4">
            <p className="text-sm text-stone-600">Do you feel this conflict can skip Communication and go directly to Explore Alternatives? All participants must agree to skip.</p>
            <div className="grid grid-cols-2 gap-4">
              <button onClick={() => handleSkipCommunicationVote(true)} className="py-6 rounded-xl font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98] flex flex-col items-center gap-2 bg-white border-2 border-green-300 text-green-700 hover:bg-green-50"><ChevronRight className="w-8 h-8" /><div className="text-lg">Skip to Explore Alternatives</div><div className="text-xs opacity-80">We understand each other</div></button>
              <button onClick={() => handleSkipCommunicationVote(false)} className="py-6 rounded-xl font-medium transition-all transform hover:scale-[1.02] active:scale-[0.98] flex flex-col items-center gap-2 bg-white border-2 border-orange-300 text-orange-700 hover:bg-orange-50"><MessageSquare className="w-8 h-8" /><div className="text-lg">Continue to Communication</div><div className="text-xs opacity-80">We need to discuss further</div></button>
            </div>
            {vCount > 0 && (<div className="p-3 bg-stone-50 rounded-lg border border-stone-200"><div className="text-xs font-medium text-stone-600 mb-1">Votes so far ({vCount} of {mIds.length}):</div><div className="space-y-1">{Object.values(votes).map((vote, idx) => (<div key={idx} className="flex items-center gap-2 text-xs text-stone-600">{vote.skip ? <ChevronRight className="w-3 h-3 text-green-700" /> : <MessageSquare className="w-3 h-3 text-orange-600" />}{vote.userEmail || vote.userName} voted</div>))}</div></div>)}
          </div>
        ) : (
          <div className="p-6 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 text-blue-800 mb-2"><Clock className="w-5 h-5" /><span className="font-medium">Waiting for Other Participants</span></div>
            <p className="text-sm text-blue-700 mb-3">{myVote ? <>You voted to <strong>{myVote.skip ? 'skip to Explore Alternatives' : 'continue to Communication'}</strong>. Waiting for {mIds.length - vCount} more participant{mIds.length - vCount !== 1 ? 's' : ''} to vote.</> : <>Waiting for {mIds.length - vCount} participant{mIds.length - vCount !== 1 ? 's' : ''} to vote.</>}</p>
            <div className="p-3 bg-white rounded-lg border border-blue-100">
              <div className="text-xs font-medium text-stone-600 mb-1">Votes ({vCount} of {mIds.length}):</div>
              <div className="space-y-1">
                {Object.values(votes).map((vote, idx) => (<div key={idx} className="flex items-center gap-2 text-xs">{vote.skip ? <ChevronRight className="w-3 h-3 text-green-700" /> : <MessageSquare className="w-3 h-3 text-orange-600" />}<span className={vote.skip ? 'text-green-700' : 'text-orange-700'}>{vote.userEmail || vote.userName}: {vote.skip ? 'Skip' : 'Communicate'}</span></div>))}
                {mIds.filter(id => !votes[id]).map((id, idx) => { const m = conflict.mentees?.find(x => x.id === id); return (<div key={`p-${idx}`} className="flex items-center gap-2 text-xs text-stone-500"><Clock className="w-3 h-3" />{m?.email || m?.name || 'Participant'}: pending</div>); })}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  })();

  // === CHALLENGES SECTION ===
  const [challengesOpen, setChallengesOpen] = useState(false);
  const sectionChallenges = userResponse ? (
    <div className="border border-stone-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setChallengesOpen(!challengesOpen)}
        className="w-full flex items-center justify-between gap-2 p-4 bg-stone-50 hover:bg-stone-100 transition-colors text-left"
      >
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-700" />
          <h4 className="font-medium text-stone-800">Challenges</h4>
          <span className="text-xs text-stone-500">(optional)</span>
          {challenges.length > 0 && (
            <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-medium">{challenges.length} added</span>
          )}
        </div>
        {challengesOpen ? <ChevronUp className="w-4 h-4 text-stone-500" /> : <ChevronDown className="w-4 h-4 text-stone-500" />}
      </button>
      {challengesOpen && <div className="p-4 border-t border-stone-200">
      <p className="text-sm text-stone-600 mb-4">
        Are there any practical constraints (like budget, time, living situation, or other factors) that could make resolving this conflict harder? Sharing these now helps everyone propose realistic solutions later.
      </p>

      {/* Existing challenges from all users */}
      {challenges.length > 0 && (
        <div className="space-y-2 mb-4">
          {challenges.map(ch => {
            const isMine = ch.userId === user.id;
            return (
              <div key={ch.id} className={`p-3 rounded-lg border ${isMine ? 'bg-amber-50 border-amber-200' : 'bg-stone-50 border-stone-200'}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1">
                    <p className="text-sm text-stone-700">{ch.text}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-[10px] text-stone-500">{ch.userName || ch.userEmail}</span>
                      <span className="text-[10px] text-stone-300">·</span>
                      <span className="text-[10px] text-stone-500">{new Date(ch.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                    </div>
                  </div>
                  {isMine && canEdit && (
                    <button onClick={() => handleRemoveChallenge(ch.id)} className="text-stone-500 hover:text-red-500 flex-shrink-0 p-1" title="Remove">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add challenge form — only for mentees who have submitted their paraphrase */}
      {isMentee && canEdit && myChallenges.length < maxChallenges && (
        <div className="bg-white rounded-lg border-2 border-dashed border-amber-300 p-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={challengeText}
              onChange={e => setChallengeText(e.target.value)}
              placeholder="e.g., Limited budget for solutions, tight work schedule..."
              maxLength={200}
              className="flex-1 px-3 py-2 border border-stone-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-400 text-sm"
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddChallenge(); } }}
            />
            <button
              onClick={handleAddChallenge}
              disabled={submittingChallenge || !challengeText.trim() || challengeText.trim().length < 10}
              className="px-4 py-2 bg-amber-700 hover:bg-amber-800 disabled:bg-stone-300 text-white text-sm rounded-lg font-medium transition-colors flex-shrink-0"
            >
              {submittingChallenge ? '...' : 'Add'}
            </button>
          </div>
          <div className="flex justify-between mt-1.5">
            <span className="text-[10px] text-stone-500">{challengeText.length}/200 characters {challengeText.length > 0 && challengeText.length < 10 ? `(need ${10 - challengeText.length} more)` : ''}</span>
            <span className="text-[10px] text-stone-500">{myChallenges.length}/{maxChallenges} added</span>
          </div>
        </div>
      )}

      {myChallenges.length >= maxChallenges && (
        <p className="text-xs text-stone-500 mt-2">You've added the maximum of {maxChallenges} challenges. Remove one to add a different one.</p>
      )}

      {challenges.length === 0 && (!isMentee || !canEdit) && (
        <p className="text-sm text-stone-500 italic text-center py-4">No challenges have been identified yet.</p>
      )}
      </div>}
    </div>
  ) : null;

  // === RENDER — order flips after submission ===
  return (
    <div className="space-y-6">
      {!userResponse ? (
        <>
          {sectionProblemStatement}
          {sectionStep1}
          {sectionChallenges}
          {sectionStatus}
          {sectionSkipVote}
        </>
      ) : (
        <>
          {sectionSkipVote}
          {sectionStatus}
          {sectionStep1}
          {sectionProblemStatement}
          {sectionChallenges}
        </>
      )}
    </div>
  );
}

// Communicate Step

export default IdentifyDefineStep;
